"use client";

import React, { useState, useRef, useEffect } from "react";
import { useUniversalAuth } from "@west-tennessee-consulting/ui";
import { useSignOut } from "@/lib/utils/auth/sign-out";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../dropdown-menu";
import { useTheme } from "@/lib/utils/formatting/theme";
import { useFeatureFlags } from "@/lib/contexts/feature-flags-context";
import { isSupportChatChannelConfigured } from "@/lib/feature-flags";
import { useSupportChatOpenOptional } from "@/lib/contexts/support-chat-open-context";
import {
  getDisplayName,
  getUserInitials,
  getUserTypeDisplay,
} from "./user-profile-menu/types";
import type { UserProfileMenuProps } from "./user-profile-menu/types";
import { UserProfileTrigger } from "./user-profile-menu/UserProfileTrigger";
import { UserProfileHeader } from "./user-profile-menu/UserProfileHeader";
import { AccountSection } from "./user-profile-menu/AccountSection";
import { PreferencesSection } from "./user-profile-menu/PreferencesSection";
import { SignOutItem } from "./user-profile-menu/SignOutItem";
import { HiddenNavCustomization } from "./user-profile-menu/HiddenNavCustomization";
import { ProfilePictureModal } from "@/components/account/ProfilePictureModal";
import { useProfileAvatar } from "@/lib/hooks/user/use-profile-avatar";

// Re-export type for external consumers
export type { UserProfileMenuProps } from "./user-profile-menu/types";

export function UserProfileMenu({
  navigationItems = [],
  visibleItems = [],
  hiddenItems = [],
  onSave = () => {},
  onReset = () => {},
  onToggleVisibility = () => {},
  onMoveUp = () => {},
  onMoveDown = () => {},
  onMoveChildUp = () => {},
  onMoveChildDown = () => {},
  onToggleChildVisibility = () => {},
}: UserProfileMenuProps) {
  const { appUser, isClientUser, isEmployee, isAdmin } = useUniversalAuth();
  const { signOut } = useSignOut();
  const { theme, toggleTheme } = useTheme();
  const { isFeatureEnabled } = useFeatureFlags();
  const supportChatContext = useSupportChatOpenOptional();
  const { avatarUrl } = useProfileAvatar();

  // State for navigation customization modal
  const [isNavCustomizationOpen, setIsNavCustomizationOpen] = useState(false);
  const navCustomizationButtonRef = useRef<HTMLDivElement>(null);

  // Controlled menu + profile-picture modal: clicking your photo closes the
  // menu and opens the manage-picture modal.
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isPictureModalOpen, setIsPictureModalOpen] = useState(false);

  // Effect to trigger the hidden navigation customization when state changes
  useEffect(() => {
    if (isNavCustomizationOpen && navCustomizationButtonRef.current) {
      navCustomizationButtonRef.current.click();
      setIsNavCustomizationOpen(false);
    }
  }, [isNavCustomizationOpen]);

  const userTypeDisplay = getUserTypeDisplay({ isAdmin, isEmployee, isClientUser });
  const initials = getUserInitials(appUser);
  const displayName = getDisplayName(appUser);

  const handleSignOut = async () => {
    await signOut();
  };

  return (
    <>
      <DropdownMenu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
        <DropdownMenuTrigger asChild>
          <UserProfileTrigger
            initials={initials}
            displayName={displayName}
            userTypeDisplay={userTypeDisplay}
            avatarUrl={avatarUrl}
          />
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-64 shadow-lg border border-border">
          {/* User Info Header */}
          <UserProfileHeader
            initials={initials}
            displayName={displayName}
            email={appUser?.email}
            userTypeDisplay={userTypeDisplay}
            avatarUrl={avatarUrl}
            onManagePicture={() => {
              setIsMenuOpen(false);
              setIsPictureModalOpen(true);
            }}
          />

          <AccountSection isEmployee={isEmployee} isAdmin={isAdmin} />

          <DropdownMenuSeparator />

          <PreferencesSection
            theme={theme}
            toggleTheme={toggleTheme}
            isEmployee={isEmployee}
            isAdmin={isAdmin}
            hasNavigationItems={navigationItems.length > 0}
            onOpenNavCustomization={() => setIsNavCustomizationOpen(true)}
            // Show "Live chat" only when the widget can actually open: the
            // flag is on AND a Slack channel is configured. Without the channel
            // check the item appeared but SupportChatButton rendered null, so
            // the click silently did nothing. Keep this aligned with the widget
            // mount gate in app/layout.tsx (isSupportChatAvailable).
            isSupportChatEnabled={
              isFeatureEnabled("supportChat") && isSupportChatChannelConfigured()
            }
            onOpenSupportChat={() => supportChatContext?.setOpen(true)}
          />

          <DropdownMenuSeparator />

          {/* Sign Out */}
          <SignOutItem onSignOut={handleSignOut} />
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Hidden Navigation Customization Modal - Triggered via state */}
      {(isEmployee || isAdmin) && navigationItems.length > 0 && (
        <HiddenNavCustomization
          navigationItems={navigationItems}
          visibleItems={visibleItems}
          hiddenItems={hiddenItems}
          onSave={onSave}
          onReset={onReset}
          onToggleVisibility={onToggleVisibility}
          onMoveUp={onMoveUp}
          onMoveDown={onMoveDown}
          onMoveChildUp={onMoveChildUp}
          onMoveChildDown={onMoveChildDown}
          onToggleChildVisibility={onToggleChildVisibility}
          triggerRef={navCustomizationButtonRef}
        />
      )}

      {/* Manage profile picture — opened from the dropdown header avatar */}
      <ProfilePictureModal open={isPictureModalOpen} onOpenChange={setIsPictureModalOpen} />
    </>
  );
}
